import {
	ChevronDownIcon,
	ChevronRightIcon,
	DownloadIcon,
} from "@chakra-ui/icons";
import {
	Box,
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Button,
	Flex,
	Grid,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Step,
	Text,
	StepIcon,
	StepIndicator,
	Stepper,
	StepSeparator,
	StepStatus,
	StepTitle,
	useDisclosure,
	useSteps,
	Badge,
	StepDescription,
	MenuGroup,
	Avatar,
} from "@chakra-ui/react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import {
	useCommand,
	useCommandActivityLogsPaginated,
	useCommandTickets,
} from "../hooks/commands.hooks";
import Card from "../components/Card";
import CardTitle from "../components/CardTitle";
import CardBody from "../components/CardBody";
import PageTitle from "../components/PageTitle";
import { CommandStatus } from "../types/command.types";
import { PurchaseOrderDeliveryType } from "../types/purchase_orders.types";
import HaltIcon from "../components/icons/HaltIcon";
import PenIcon from "../components/icons/PenIcon";
import TrashIcon from "../components/icons/TrashIcon";
import HaltOrderModal from "../components/modals/HaltOrderModal";
import { addBusinessDays, format } from "date-fns";
import UpdateOrderModal from "../components/modals/UpdateOrderModal";
import DeleteOrderModal from "../components/modals/DeleteOrderModal";
import { useCenter } from "../hooks/centers.hooks";
import { useMemo, useRef } from "react";
import EarBadge from "../components/EarBadge";
import {
	NewOrderEarSide,
	NewOrderStep,
	useNewOrderStore,
} from "../stores/order.store";
import CopyOnClick from "../components/CopyOnClick";
import CommandTypeBadge from "../components/CommandTypeBadge";
import InlineUserAvatar from "../components/InlineUserAvatar";
import RightEarCard from "../components/RightEarCard";
import LeftEarCard from "../components/LeftEarCard";
import TicketIcon from "../components/icons/TicketIcon";
import FileIconPdf from "../components/icons/files/FileIconPdf";
import { fetchFileContent } from "../mutations/file.mutations";
import { useMutation, useQueryClient } from "react-query";
import type { SavedFile } from "../types/files.types";
import {
	type ActivityLogChangeStatusMeta,
	type ActivityLogHistoryMeta,
	ActivityLogType,
} from "../types/logs.types";
import i18n from "../i18n";
import { useConnectedUser } from "../hooks/user.hooks";
import {
	changeCommandStatus,
	updateCommand,
} from "../mutations/command.mutations";
import QrCodeIcon from "../components/icons/QrCodeIcon";
import ReworkIcon from "../components/icons/ReworkIcon";
import DiscountIcon from "../components/icons/DiscountIcon";
import SimpleModal from "../components/modals/SimpleModal";
import OrderDiscountForm, {
	type OrderDiscountFormHandler,
} from "../components/forms/OrderDiscountForm";
import OrdersDetailTicket from "./OrdersDetailTicket";
import { printFileById } from "../hooks/printers.hooks";
import { type MutateErrors, mutateOnError } from "../lib/generic";
import CommandStatusBadge from "../components/CommandStatusBadge";
import { fPhoneNumber } from "../lib/format";
import {
	getLongExposedId,
	getTicketCommandId,
} from "../services/command.service";
import { useSimpleToast } from "../hooks/toast.hooks";
import BrandFullCheckIcon from "../components/icons/BrandFullCheckIcon";
import DocumentIcon from "../components/icons/DocumentIcon";
import { useAdminSettings } from "../hooks/admin_settings.hooks";
import { getCommandMaterialContext } from "../utils/getCommandMaterialContext";
import CommandPriorityBadge from "../components/badges/CommandPriorityBadge";

function OrdersDetailProcessStateStepperComplete() {
	return <StepIcon></StepIcon>;
}

function OrdersDetailProcessStateStepperIncomplete() {
	return <Box bgColor="#D0D5DD" w={2} h={2} borderRadius="3rem"></Box>;
}

function OrdersDetailProcessStateStepperActive() {
	return (
		<Flex
			bgColor="#D0D5DD"
			w={8}
			h={8}
			borderRadius="3rem"
			justifyContent={"center"}
			alignItems={"center"}
		>
			<Flex
				bgColor="brand.500"
				w={8}
				h={8}
				borderRadius="3rem"
				justifyContent={"center"}
				alignItems={"center"}
			>
				<Box bgColor="white" w={2} h={2} borderRadius="3rem"></Box>
			</Flex>
		</Flex>
	);
}

function OrdersDetailProcessStateStepper({ state }: { state: CommandStatus }) {
	const steps = [
		{
			title: "En attente de réception",
			state: CommandStatus.WAITING_FOR_RECEPTION,
		},
		{ title: "Réceptionné", state: CommandStatus.RECEIVED },
		{ title: "En modélisation", state: CommandStatus.MODELING },
		{ title: "En impression", state: CommandStatus.PRINTING },
		{ title: "En injection pour silicone", state: CommandStatus.INJECTING },
		{ title: "En finition", state: CommandStatus.POLISHING },
		{ title: "Contrôle qualité", state: CommandStatus.QUALITY_CONTROL },
		{ title: "Prêt à expédier", state: CommandStatus.READY_FOR_DELIVERY },
		{ title: "Expédié", state: CommandStatus.IN_DELIVERY },
		{ title: "Livré", state: CommandStatus.DELIVERED },
	];
	const { activeStep } = useSteps({
		index: steps.findIndex((step) => step.state === state),
		count: steps.length,
	});

	return (
		<Stepper
			index={activeStep}
			orientation="vertical"
			height="500px"
			gap="0"
			colorScheme="brand"
		>
			{steps.map((step, index) => (
				<Step key={index}>
					<StepIndicator>
						<StepStatus
							complete={<OrdersDetailProcessStateStepperComplete />}
							incomplete={<OrdersDetailProcessStateStepperIncomplete />}
							active={<OrdersDetailProcessStateStepperActive />}
						/>
					</StepIndicator>

					<Box position="absolute" top="4px" left="40px" w="240px">
						<StepTitle>{step.title}</StepTitle>
					</Box>

					<StepSeparator />
				</Step>
			))}
		</Stepper>
	);
}

function OrdersDetailActivityLogsStepper({ id }: { id: number }) {
	const logs = useCommandActivityLogsPaginated({ id })(
		1,
		1000,
		undefined,
		{
			type: "CHANGE_STATUS,UPDATE,DISCOUNT,",
		},
		["-createdAt"],
	);
	const logsLength = useMemo(() => (logs?.data ? logs.data.length : 0), [logs]);

	const handlePrevSwitch = (meta: ActivityLogChangeStatusMeta) => {
		if (meta.prev) {
			return meta.prev ?? "";
		}
		return meta.from ?? "";
	};
	const handleNextSwitch = (meta: ActivityLogChangeStatusMeta) => {
		if (meta.next) {
			return meta.next ?? "";
		}
		return meta.to ?? "";
	};
	return (
		<Stepper
			index={0}
			orientation="vertical"
			height={`${70 * logsLength}px`}
			mb="20px"
			gap="0"
			colorScheme="brand"
		>
			{logs?.data &&
				logs.data.map((log, index) => (
					<Step key={index}>
						<StepIndicator>
							<StepStatus
								complete={
									<Avatar
										name={`${log.user?.firstName} ${log.user?.lastName}`}
										src="https://bit.ly/cbouchard"
										h={8}
										w={8}
									/>
								}
								incomplete={
									<Avatar
										name={`${log.user?.firstName} ${log.user?.lastName}`}
										src="https://bit.ly/cbouchard"
										h={8}
										w={8}
									/>
								}
								active={
									<Avatar
										name={`${log.user?.firstName} ${log.user?.lastName}`}
										src="https://bit.ly/cbouchard"
										h={8}
										w={8}
									/>
								}
							/>
						</StepIndicator>

						{log.type === ActivityLogType.CHANGE_STATUS && (
							<Box position="absolute" top="4px" left="40px" w="300px">
								<StepDescription>
									<Flex direction="row" gap={2}>
										<Text textTransform="capitalize">
											{log.user?.firstName} {log.user?.lastName}
										</Text>
										<Text>-</Text>
										<Text>{format(log.createdAt, "HH:mm dd/MM/yyyy")}</Text>
									</Flex>
									<Flex direction="row" gap={2}>
										<Text>
											Le statut de la commande est passé de{" "}
											<Text color="brand.700" display="inline">
												{i18n.t(
													`command.status.${handlePrevSwitch(
														log.meta as ActivityLogChangeStatusMeta,
													)}`,
												)}
											</Text>{" "}
											à{" "}
											<Text color="brand.700" display="inline">
												{i18n.t(
													`command.status.${handleNextSwitch(
														log.meta as ActivityLogChangeStatusMeta,
													)}`,
												)}
											</Text>
										</Text>
									</Flex>
								</StepDescription>
							</Box>
						)}

						{log.type === ActivityLogType.UPDATE && (
							<Box position="absolute" top="4px" left="40px" w="3000px">
								<StepDescription>
									<Flex direction="row" gap={2}>
										<Text textTransform="capitalize">
											{log.user?.firstName} {log.user?.lastName}
										</Text>
										<Text>-</Text>
										<Text>{format(log.createdAt, "HH:mm dd/MM/yyyy")}</Text>
									</Flex>
									<Flex direction="row" gap={2}>
										<Text>La commande a été mise à jour</Text>
									</Flex>
								</StepDescription>
							</Box>
						)}

						{log.type === ActivityLogType.DISCOUNT && (
							<Box position="absolute" top="4px" left="40px" w="280px">
								<StepDescription>
									<Flex
										direction="row"
										gap={2}
										alignItems="center"
										textColor="green.600"
									>
										<BrandFullCheckIcon />
										<Text fontSize="14px" fontWeight={600}>
											Remise de {(log.meta as { value: number }).value}%
										</Text>
									</Flex>
									<Flex direction="row-reverse" gap={2}>
										<Text>{format(log.createdAt, "HH:mm dd/MM/yyyy")}</Text>
									</Flex>
								</StepDescription>
							</Box>
						)}

						<StepSeparator />
					</Step>
				))}
		</Stepper>
	);
}

function OrdersDetailHistoryLogsStepper({ id }: { id: number }) {
	const logs = useCommandActivityLogsPaginated({ id })(
		1,
		1000,
		undefined,
		{
			type: "HISTORY",
		},
		["-createdAt"],
	);
	const logsLength = useMemo(() => (logs?.data ? logs.data.length : 0), [logs]);
	const { activeStep } = useSteps({
		index: logsLength + 1,
		count: logsLength,
	});

	return (
		<Stepper
			index={activeStep}
			orientation="vertical"
			height={`${50 * logsLength}px`}
			mb="20px"
			gap="0"
			colorScheme="brand"
		>
			{logs?.data &&
				logs.data.map((log, index) => (
					<Step key={index}>
						<StepIndicator>
							<StepStatus
								complete={<OrdersDetailProcessStateStepperComplete />}
								incomplete={<OrdersDetailProcessStateStepperIncomplete />}
								active={<OrdersDetailProcessStateStepperActive />}
							/>
						</StepIndicator>

						{log.type === ActivityLogType.HISTORY && (
							<Box position="absolute" top="4px" left="40px" w="300px">
								<StepTitle>
									{i18n.t(
										`activity_log.type.${(log.meta as ActivityLogHistoryMeta).type}`,
									)}
								</StepTitle>
								<StepDescription>
									<Flex direction="row" gap={2}>
										<Text>
											{format(log.createdAt, "HH:mm dd/MM/yyyy")}
											{" - "}
											{log.user?.firstName} {log.user?.lastName}
										</Text>
									</Flex>
								</StepDescription>
							</Box>
						)}

						<StepSeparator />
					</Step>
				))}
		</Stepper>
	);
}

function OrdersCommandDetailPage() {
	const { id } = useParams();
	const { load, setStep, setIsUpdate } = useNewOrderStore();
	const { data: command } = useCommand(id ?? null);
	const { data: adminSettings } = useAdminSettings();
	const draft = useMemo(() => {
		if (command && command.draft) load({ ...command.draft, command: command });
		return command?.draft;
	}, [command, load]);
	const { data: center } = useCenter(command?.centerId);
	const navigate = useNavigate();
	const {
		isOpen: haltModalIsOpen,
		onOpen: haltModalOnOpen,
		onClose: haltModalOnClose,
	} = useDisclosure();
	const {
		isOpen: updateModalIsOpen,
		onOpen: updateModalOnOpen,
		onClose: updateModalOnClose,
	} = useDisclosure();
	const {
		isOpen: deleteModalIsOpen,
		onOpen: deleteModalOnOpen,
		onClose: deleteModalOnClose,
	} = useDisclosure();
	const discountModal = useDisclosure();
	const discountModalRef = useRef<OrderDiscountFormHandler>(null);
	const user = useConnectedUser();
	const { tickets } = useCommandTickets(id);
	const selectedFile = useRef<SavedFile | null>(null);
	const selectedFilename = useRef<string>("");
	const { mutate: downloadFileContent } = useMutation(fetchFileContent, {
		onSuccess: (content: ArrayBuffer) => {
			if (selectedFile?.current !== null) {
				const blob = new Blob([content]);
				const link = document.createElement("a");
				link.href = URL.createObjectURL(blob);
				link.download = selectedFilename.current ?? "fichier.pdf";
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				selectedFile.current = null;
				selectedFilename.current = "";
			}
		},
		onError: (err: MutateErrors) => {
			mutateOnError(err);
			selectedFile.current = null;
			selectedFilename.current = "";
		},
	});
	const { reset, reworkFromCommand } = useNewOrderStore();
	const logs = useCommandActivityLogsPaginated({ id })(
		1,
		1000,
		undefined,
		{
			type: "CHANGE_STATUS",
		},
		["-createdAt"],
	);
	const hasActivity = useMemo(
		() => Array.isArray(logs?.data) && logs.data.length !== 0,
		[logs],
	);
	const hasComment = draft?.commentary != undefined;
	const queryClient = useQueryClient();
	const { mutate: mutateCommandStatus } = useMutation(changeCommandStatus, {
		onSuccess: async () => {
			await queryClient.invalidateQueries();
		},
	});
	const handleHalt = () => {
		if (user?.isSuperAdmin) {
			if (id) {
				mutateCommandStatus({
					id: Number.parseInt(id),
					status: "HALTED",
					reason: "",
					explanation: "",
				});
			}
		} else {
			haltModalOnOpen();
		}
	};
	const handleUpdateOrder = () => {
		if (user?.isSuperAdmin) {
			if (command?.draft) {
				reset();
				load({ ...command.draft, command: command });
				setIsUpdate(true);
				setStep(NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE);
				navigate("/orders/new");
			}
		} else {
			updateModalOnOpen();
		}
	};
	const handleDiscount = () => {
		discountModal.onOpen();
	};
	const { mutate: mutateCommandUpdate } = useMutation(updateCommand, {
		onSuccess: async () => {
			await queryClient.invalidateQueries();
		},
	});
	const toast = useSimpleToast();
	const handleChangeStatus = (status: CommandStatus) => {
		if (
			status === CommandStatus.INJECTING &&
			command &&
			getCommandMaterialContext(command).canInjectSilicone === false
		) {
			toast.error(
				"Command",
				"Seules les commandes avec au moins une oreille en silicone peuvent être en injection.",
			);
			return;
		}
		mutateCommandUpdate(
			{
				id: command?.id,
				status,
			},
			{
				onSuccess: () => {
					toast.success("Command", "Statut mis à jour avec succès");
				},
				onError: () => {
					toast.error("Command", "Erreur lors de la mise à jour du statut");
				},
			},
		);
	};

	if (command === null || command === undefined) {
		return <>---</>;
	}
	const shippingDate =
		command.groupExpectedShippingDate ?? command.expectedShippingDate;
	const hasShippingDate =
		shippingDate && !Number.isNaN(new Date(shippingDate).getTime());
	const estimatedDeliveryDate =
		hasShippingDate && adminSettings?.meanDaysShippingTime != null
			? addBusinessDays(
					new Date(shippingDate),
					adminSettings.meanDaysShippingTime,
				)
			: hasShippingDate
				? new Date(shippingDate)
				: null;

	return (
		<>
			<Breadcrumb
				spacing="8px"
				separator={<ChevronRightIcon color="gray.500" />}
			>
				<BreadcrumbItem>
					<BreadcrumbLink
						href="/orders"
						color="gray.600"
						fontWeight={600}
						fontSize="14px"
					>
						Commandes
					</BreadcrumbLink>
				</BreadcrumbItem>

				{center && (
					<BreadcrumbItem>
						<BreadcrumbLink
							href={`/contacts/centers/${center.id}`}
							color="gray.600"
							fontWeight={600}
							fontSize="14px"
						>
							{center.name}
						</BreadcrumbLink>
					</BreadcrumbItem>
				)}

				<BreadcrumbItem>
					<BreadcrumbLink
						href="#"
						color="brand.700"
						fontWeight={600}
						fontSize="14px"
					>
						{id}
					</BreadcrumbLink>
				</BreadcrumbItem>
			</Breadcrumb>

			{/* HEADER */}
			<Flex justifyContent="space-between">
				<Flex alignItems={"center"} gap={4}>
					<CopyOnClick content={getLongExposedId(command)}>
						<PageTitle>{getLongExposedId(command)}</PageTitle>
					</CopyOnClick>
					<CommandStatusBadge id={command.id} />
					{command.isPriority ? <CommandPriorityBadge /> : null}
				</Flex>
				<Flex gap={3}>
					<Menu>
						{user?.isSuperAdmin && (
							<MenuButton
								as={Button}
								bg="white"
								rightIcon={<ChevronDownIcon />}
							>
								Changer le statut
							</MenuButton>
						)}
						<MenuList>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.WAITING_FOR_RECEPTION)
								}
							>
								En attente de réception
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.RECEIVED)}
							>
								Réceptionné
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.WAITING_FOR_MODELING)
								}
							>
								En attente de modélisation
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.MODELING)}
							>
								En modélisation
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.WAITING_FOR_PRINTING)
								}
							>
								Prête pour impression
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.PRINTING)}
							>
								En impression
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.PRINTED)}
							>
								Imprimé
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.WAITING_FOR_LAB)
								}
							>
								À traiter labo
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.INJECTING)}
							>
								En injection silicone
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.POLISHING)}
							>
								En finition
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.QUALITY_CONTROL)
								}
							>
								Contrôle qualité
							</MenuItem>
							<MenuItem onClick={() => handleChangeStatus(CommandStatus.READY)}>
								Prête
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.READY_FOR_DELIVERY)
								}
							>
								Prêt à expédier
							</MenuItem>
							<MenuItem
								onClick={() =>
									handleChangeStatus(CommandStatus.PARCEL_PREPARED)
								}
							>
								Colis préparé
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.IN_DELIVERY)}
							>
								Expédiée
							</MenuItem>
							<MenuItem
								onClick={() => handleChangeStatus(CommandStatus.DELIVERED)}
							>
								Livré
							</MenuItem>
						</MenuList>
					</Menu>
					<Menu>
						<MenuButton
							as={Button}
							bg="brand.600"
							color="white"
							rightIcon={<ChevronDownIcon />}
						>
							Actions
						</MenuButton>
						<MenuList>
							<MenuGroup title="Gestion de la commande">
								<MenuItem
									onClick={() => {
										if (command === null || command === undefined) {
											return;
										}
										reset();
										reworkFromCommand(command);
										navigate("/orders/new");
									}}
								>
									<ReworkIcon mr={3} />
									Créer une refonte
								</MenuItem>
								<MenuItem
									onClick={() =>
										navigate(
											`/tickets/new?commandId=${getTicketCommandId(command)}`,
										)
									}
								>
									<TicketIcon mr={3} />
									Créer un ticket
								</MenuItem>
								{user?.isSuperAdmin && (
									<MenuItem onClick={() => handleDiscount()}>
										<DiscountIcon mr={3} />
										Faire une remise
									</MenuItem>
								)}
								{command.haltedAt === null && (
									<MenuItem onClick={() => handleHalt()}>
										<HaltIcon mr={3}></HaltIcon>
										{user?.isSuperAdmin
											? "Mettre en pause"
											: "Demande de mise en pause"}
									</MenuItem>
								)}
								<MenuItem onClick={() => handleUpdateOrder()}>
									<PenIcon mr={3}></PenIcon>
									{user?.isSuperAdmin
										? "Modifier la commande"
										: "Demande de modification"}
								</MenuItem>
								<MenuItem onClick={() => deleteModalOnOpen()} color="#B91C1C">
									<TrashIcon mr={3}></TrashIcon>
									{user?.isSuperAdmin
										? "Annuler la commande"
										: "Demande d'annulation de la commande"}
								</MenuItem>
							</MenuGroup>
							<MenuGroup title="Documents">
								<MenuItem
									onClick={() => {
										if (
											command.purchaseOrderFileId &&
											command.purchaseOrderFile
										) {
											selectedFile.current = command.purchaseOrderFile;
											selectedFilename.current = `Commande-${getLongExposedId(command)}.pdf`;
											downloadFileContent(command.purchaseOrderFileId);
										}
									}}
								>
									<DownloadIcon mr={3} />
									Télécharger le bon de commande
								</MenuItem>
								{user?.isSuperAdmin && command.purchaseOrderFileId && (
									<MenuItem
										onClick={() => {
											printFileById(
												command.purchaseOrderFileId!,
												"Imprimante",
												"test",
												{},
											);
										}}
									>
										<DocumentIcon mr={3} />
										Imprimer le bon de commande
									</MenuItem>
								)}
								{user?.isSuperAdmin && command.labelFileId && (
									<MenuItem
										onClick={() =>
											printFileById(
												command.labelFileId!,
												"Imprimante Etiquettes",
												"test",
												{},
											)
										}
									>
										<QrCodeIcon mr={3} />
										Imprimer l'étiquette
									</MenuItem>
								)}
							</MenuGroup>
						</MenuList>
					</Menu>
				</Flex>
			</Flex>
			{command.discount !== null && command.discount !== 0 ? (
				<Flex mb={4} color="green">
					Remise de {command.discount}%
				</Flex>
			) : (
				""
			)}
			<Flex mb={4}>
				<Flex alignItems={"center"} gap={4}>
					{draft?.hasLeftEar && <EarBadge side={NewOrderEarSide.LEFT} mr={1} />}
					{draft?.hasRightEar && <EarBadge side={NewOrderEarSide.RIGHT} />}
					{command.type && (
						<Box>
							<CommandTypeBadge type={command.type} />
						</Box>
					)}
					{command.modelerId && (
						<Flex alignItems="center" gap={4}>
							<Text>Assigné à</Text>
							<InlineUserAvatar id={command.modelerId} w="24px" h="24px" />
						</Flex>
					)}
				</Flex>
			</Flex>
			{/* BODY */}
			<Flex w="100%" gap={3}>
				<Flex direction="column" flexGrow={2} gap={3}>
					<Card>
						<CardTitle>
							<Flex justifyContent="space-between">
								<Box fontWeight={600} fontSize="18px">
									Détails de la commande
								</Box>
							</Flex>
						</CardTitle>
						<CardBody>
							<Flex
								justifyContent="space-between"
								borderBottom="solid 1px"
								borderColor="gray.200"
								py={2}
								fontSize={"14px"}
							>
								<Box>ID</Box>
								<Box>{command.id}</Box>
							</Flex>
							<Flex
								justifyContent="space-between"
								borderBottom="solid 1px"
								borderColor="gray.200"
								py={2}
								fontSize={"14px"}
							>
								<Box>Date de commande</Box>
								<Box>{format(command.createdAt, "dd/MM/yyyy")}</Box>
							</Flex>
							<Flex
								justifyContent="space-between"
								borderBottom="solid 1px"
								borderColor="gray.200"
								py={2}
								fontSize={"14px"}
							>
								<Box>Date d'expedition</Box>
								<Box>
									{hasShippingDate
										? format(new Date(shippingDate), "dd/MM/yyyy")
										: "Pas encore reçu"}
								</Box>
							</Flex>
							<Flex
								justifyContent="space-between"
								borderBottom="solid 1px"
								borderColor="gray.200"
								py={2}
								fontSize={"14px"}
							>
								<Box>Date estimative de livraison</Box>
								<Box>
									{estimatedDeliveryDate
										? format(estimatedDeliveryDate, "dd/MM/yyyy")
										: "Pas encore reçu"}
								</Box>
							</Flex>

							<Flex
								justifyContent="space-between"
								borderBottom="solid 1px"
								borderColor="gray.200"
								py={2}
								fontSize={"14px"}
							>
								<Box>Audioprothésiste responsable</Box>
								<Box>
									<InlineUserAvatar
										id={command?.managerId}
										h="24px"
										w="24px"
										link
									/>
								</Box>
							</Flex>

							<Flex
								justifyContent="space-between"
								borderBottom="solid 1px"
								borderColor="gray.200"
								py={2}
								fontSize={"14px"}
							>
								<Box>Créé par</Box>
								<Box>
									<InlineUserAvatar
										id={command?.creatorId}
										h="24px"
										w="24px"
										link
									/>
								</Box>
							</Flex>

							<Flex
								justifyContent="space-between"
								borderBottom="solid 1px"
								borderColor="gray.200"
								py={2}
								fontSize={"14px"}
							>
								<Box>Type des empreintes</Box>
								<Box>
									<CommandTypeBadge type={command.type} />
								</Box>
							</Flex>

							<Flex justifyContent="space-between" py={2} fontSize={"14px"}>
								<Box>Type de commande</Box>
								{draft && draft.type === "NEW" && <Box>Commande standard</Box>}
								{draft && draft.type === "REWORK" && <Box>Refonte</Box>}
							</Flex>

							{draft && draft.type === "REWORK" && (
								<Flex
									borderTop="solid 1px"
									borderColor="gray.200"
									justifyContent="space-between"
									py={2}
									fontSize={"14px"}
								>
									<Box>Raison de la refonte</Box>
									<Box>
										{draft.renewReason
											? i18n.t(draft.renewReason.toLowerCase())
											: "--"}
									</Box>
								</Flex>
							)}
							{draft && draft.type === "REWORK" && (
								<Flex
									borderTop="solid 1px"
									borderColor="gray.200"
									justifyContent="space-between"
									py={2}
									fontSize={"14px"}
								>
									<Box>Précédente commande</Box>
									{draft.reworkCommandOld && (
										<Box>{i18n.t(draft.reworkCommandOld)}</Box>
									)}
									{draft.reworkCommand && (
										<NavLink to={`/orders/${draft.reworkCommand.id}/`}>
											<Box textDecoration="underline" color="brand.700">
												{i18n.t(draft.reworkCommand.exposedId)}
											</Box>
										</NavLink>
									)}
								</Flex>
							)}

							{command.purchaseOrder?.deliveryType ===
							PurchaseOrderDeliveryType.UPS ? (
								<Flex
									justifyContent="space-between"
									borderBottom="solid 1px"
									borderColor="gray.200"
									py={2}
									fontSize={"14px"}
								>
									<Box>Tracking de la commande</Box>
									<Box>
										{command.purchaseOrder?.trackingId ? (
											<Text
												fontSize="14px"
												fontWeight={600}
												color="brand.700"
												_hover={{
													textDecoration: "underline",
												}}
											>
												<a
													href={`https://www.ups.com/track?tracknum=${command.purchaseOrder.trackingId}`}
													target="_blank"
												>
													Lien de tracking UPS
												</a>
											</Text>
										) : (
											<Text fontSize="14px" fontWeight={500} color="gray.500">
												Lien indisponible
											</Text>
										)}
									</Box>
								</Flex>
							) : null}
						</CardBody>
					</Card>

					<Card>
						<CardTitle>
							<Flex>
								<Box fontWeight={600} fontSize="18px">
									{center?.name ?? "Adresses de livraison et facturation"}
								</Box>
							</Flex>
						</CardTitle>

						<CardBody>
							<Flex direction="row" justify="space-between" gap={4}>
								<Flex direction="column" justify="start" flexGrow={1}>
									<Text lineHeight="27px" fontSize="18px" fontWeight="600">
										Adresse de facturation
									</Text>
									<Text>{command?.center?.enterprise?.name}</Text>
									<Text>
										{command.billingAddress?.streetNumber}{" "}
										{command.billingAddress?.streetName}
									</Text>
									<Text>
										{command.billingAddress?.postalCode}{" "}
										{command.billingAddress?.city}
									</Text>
								</Flex>
								<Box borderLeft="solid 1px" borderColor="gray.300"></Box>
								<Flex
									direction="column"
									flexGrow={1}
									onClick={() => {
										navigate(`/orders?center=${command.centerId}`);
									}}
									cursor="pointer"
								>
									<Text lineHeight="27px" fontSize="18px" fontWeight="600">
										Adresse de livraison
									</Text>
									<Text>{command?.center?.name}</Text>
									<Text>
										{command.shippingAddress?.streetNumber}{" "}
										{command.shippingAddress?.streetName}
									</Text>
									<Text>
										{command.shippingAddress?.postalCode}{" "}
										{command.shippingAddress?.city}
									</Text>
									<Text>{fPhoneNumber(command.center?.phoneNumber ?? "")}</Text>
								</Flex>
							</Flex>
						</CardBody>
					</Card>

					<Grid gridTemplateColumns={"1fr 1fr"} gap={4}>
						<RightEarCard
							earmold={command.draft?.earmold}
							form={command.draft?.rightEarForm}
						/>
						<LeftEarCard
							earmold={command.draft?.earmold}
							form={command.draft?.leftEarForm}
						/>
					</Grid>

					<Card>
						<CardTitle>
							<Flex>
								<Box fontWeight={600} fontSize="18px">
									Documents
								</Box>
							</Flex>
						</CardTitle>

						<CardBody>
							<Flex direction="column" gap={2}>
								{[
									...(Array.isArray(command.draft?.additionalFiles)
										? command.draft.additionalFiles.map((file) => ({
												file: file,
												filename: file?.fileName,
											}))
										: []),
									{
										file: command.purchaseOrderFile,
										filename: `Commande-${getLongExposedId(command)}.pdf`,
									},
									{
										file: command.purchaseOrder?.file,
										filename: command.purchaseOrder?.createdAt
											? `BL${format(
													command.purchaseOrder?.createdAt,
													"yyyy",
												)}${command.purchaseOrder?.id}.pdf`
											: "Bon de livraison.pdf",
									},
									{
										file: command.purchaseOrder?.shippingLabelFile,
										filename:
											command.purchaseOrder?.shippingLabelFile?.fileName,
									},
									{
										file: command.conformityNoteFile,
										filename: `Conformité-${getLongExposedId(command)}.pdf`,
									},
									{
										file: command.informationSheetFile,
										filename: `Fiche de renseignement.pdf`,
									},
									{
										file: command.labelFile,
										filename: `Etiquette-${getLongExposedId(command)}.pdf`,
									},
								]
									.filter(
										(file) => file.file !== undefined && file.file !== null,
									)
									.map((file, index) => (
										<Flex
											key={file.file ? file.file.id : index}
											justify="space-between"
											cursor={"pointer"}
											onClick={() => {
												if (file.file) {
													selectedFile.current = file.file;
													selectedFilename.current =
														file?.filename ?? file.file?.fileName;
													downloadFileContent(file.file.id);
												}
											}}
										>
											<Flex gap={4}>
												<FileIconPdf color="brand.700" w="24px" h="24px" />
												<Text>{file?.file?.fileName}</Text>
											</Flex>
											<DownloadIcon />
										</Flex>
									))}
							</Flex>
						</CardBody>
					</Card>

					{hasComment && (
						<Card>
							<CardTitle>
								<Flex justifyContent="space-between">
									<Box fontWeight={600} fontSize="18px">
										Commentaire
									</Box>
								</Flex>
							</CardTitle>
							<CardBody>{draft?.commentary}</CardBody>
						</Card>
					)}
				</Flex>
				<Flex
					direction="column"
					gap={3}
					flexGrow={1}
					maxW={"400px"}
					style={{ minWidth: "360px" }}
				>
					<Card>
						<CardTitle>
							<Flex justifyContent="space-between">
								<Box fontWeight={600} fontSize="18px">
									Patient
								</Box>
								<NavLink to={`/patients/${command.patientId}`}>
									<Button variant="link" color="brand.700">
										Fiche du patient
									</Button>
								</NavLink>
							</Flex>
						</CardTitle>
						<CardBody>
							<Flex
								justifyContent="space-between"
								borderBottom="solid 1px"
								borderColor="gray.200"
								py={2}
								fontSize={"14px"}
							>
								<Box>Nom</Box>
								<Box>
									{command.patient?.firstName}&nbsp;
									{command.patient?.lastName}
								</Box>
							</Flex>
							<Flex
								justifyContent="space-between"
								borderBottom="solid 1px"
								borderColor="gray.200"
								py={2}
								fontSize={"14px"}
							>
								<Box>Date de naissance</Box>
								<Box>
									{command.patient?.birthDate
										? format(command.patient?.birthDate, "dd/MM/yyyy")
										: "---"}
								</Box>
							</Flex>
							<Flex justifyContent="space-between" py={2} fontSize={"14px"}>
								<Box>N° externe de commande</Box>
								<Box>{command.externalId ?? "---"}</Box>
							</Flex>
						</CardBody>
					</Card>

					{Array.isArray(tickets) && tickets.length !== 0 && (
						<Card>
							<CardTitle>
								<Flex justifyContent="space-between">
									<Box fontWeight={600} fontSize="18px">
										Tickets
										<Badge
											variant="subtle"
											borderRadius="xl"
											ml={2}
											px="10px"
											py={"3px"}
										>
											{Array.isArray(tickets) && tickets.length}
										</Badge>
									</Box>
								</Flex>
							</CardTitle>
							<CardBody>
								<OrdersDetailTicket id={id}></OrdersDetailTicket>
							</CardBody>
						</Card>
					)}

					{hasActivity && (
						<Card>
							<CardTitle>
								<Flex justifyContent="space-between">
									<Box fontWeight={600} fontSize="18px">
										Activités liées à la commande
									</Box>
								</Flex>
							</CardTitle>
							<CardBody>
								<OrdersDetailActivityLogsStepper
									id={command.id}
								></OrdersDetailActivityLogsStepper>
							</CardBody>
						</Card>
					)}

					{user?.isSuperAdmin === false && (
						<Card>
							<CardTitle>
								<Flex justifyContent="space-between">
									<Box fontWeight={600} fontSize="18px">
										Avancée de la commande
									</Box>
								</Flex>
							</CardTitle>
							<CardBody>
								<OrdersDetailProcessStateStepper
									state={command.status}
								></OrdersDetailProcessStateStepper>
							</CardBody>
						</Card>
					)}

					<Card>
						<CardTitle>
							<Flex justifyContent="space-between">
								<Box fontWeight={600} fontSize="18px">
									Historique de commandes
								</Box>
							</Flex>
						</CardTitle>
						<CardBody>
							<OrdersDetailHistoryLogsStepper
								id={command.id}
							></OrdersDetailHistoryLogsStepper>
						</CardBody>
					</Card>
				</Flex>
			</Flex>

			<HaltOrderModal
				orderId={command.id}
				isOpen={haltModalIsOpen}
				onOpen={haltModalOnOpen}
				onClose={haltModalOnClose}
			></HaltOrderModal>
			<UpdateOrderModal
				orderId={command.id}
				isOpen={updateModalIsOpen}
				onOpen={updateModalOnOpen}
				onClose={updateModalOnClose}
			></UpdateOrderModal>
			<DeleteOrderModal
				orderId={command.id}
				isOpen={deleteModalIsOpen}
				onOpen={deleteModalOnOpen}
				onClose={deleteModalOnClose}
			></DeleteOrderModal>
			<SimpleModal
				isOpen={discountModal.isOpen}
				onClose={discountModal.onClose}
				acceptText="Appliquer"
				title="Modifier la remise de la commande"
				onClick={() => {
					if (
						discountModalRef.current === null ||
						discountModalRef.current === undefined
					) {
						return;
					}
					const values = discountModalRef.current.values();
					mutateCommandUpdate({
						id: command.id,
						discount: values.discount,
						log: true,
					});
					discountModal.onClose();
				}}
			>
				<OrderDiscountForm value={command} ref={discountModalRef} />
			</SimpleModal>
		</>
	);
}

export default OrdersCommandDetailPage;
